import React, { useMemo, useState } from 'react';
import { DailyMetrics } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

type HeatmapRange = 30 | 90;

interface MetricRow {
  key: string;
  label: string;
  getValue: (m: DailyMetrics) => number | null;
  colorScale: (val: number) => string;
  format: (val: number) => string;
}

// ─── Color scales (5-tier: red → orange → amber → green → dark green) ────────

function sleepColor(val: number): string {
  if (val >= 85) return '#059669';
  if (val >= 75) return '#34D399';
  if (val >= 60) return '#FCD34D';
  if (val >= 40) return '#FB923C';
  return '#EF4444';
}

function hrvColor(val: number): string {
  if (val >= 80) return '#059669';
  if (val >= 60) return '#34D399';
  if (val >= 40) return '#FCD34D';
  if (val >= 25) return '#FB923C';
  return '#EF4444';
}

function stepsColor(val: number): string {
  if (val >= 10000) return '#059669';
  if (val >= 7500)  return '#34D399';
  if (val >= 5000)  return '#FCD34D';
  if (val >= 2500)  return '#FB923C';
  return '#EF4444';
}

function readinessColor(val: number): string {
  if (val >= 85) return '#059669';
  if (val >= 75) return '#34D399';
  if (val >= 60) return '#FCD34D';
  if (val >= 40) return '#FB923C';
  return '#EF4444';
}

function sleepDurationColor(val: number): string {
  if (val >= 420 && val <= 540) return '#059669';
  if (val >= 360 && val <= 600) return '#34D399';
  if (val >= 300)               return '#FCD34D';
  if (val >= 240)               return '#FB923C';
  return '#EF4444';
}

// ─── Metric rows ──────────────────────────────────────────────────────────────

const METRIC_ROWS: MetricRow[] = [
  {
    key: 'sleep_score',
    label: 'Sleep',
    getValue: m => m.sleep_score,
    colorScale: sleepColor,
    format: v => `${v}/100`,
  },
  {
    key: 'hrv',
    label: 'HRV',
    getValue: m => m.hrv,
    colorScale: hrvColor,
    format: v => `${v} ms`,
  },
  {
    key: 'readiness',
    label: 'Ready',
    getValue: m => m.readiness,
    colorScale: readinessColor,
    format: v => `${v}/100`,
  },
  {
    key: 'steps',
    label: 'Steps',
    getValue: m => m.steps,
    colorScale: stepsColor,
    format: v => v.toLocaleString(),
  },
  {
    key: 'total_sleep_mins',
    label: 'Duration',
    getValue: m => m.total_sleep_mins,
    colorScale: sleepDurationColor,
    format: v => `${Math.floor(v / 60)}h ${v % 60}m`,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDates(days: number): string[] {
  const today = new Date();
  const dates: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    dates.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    );
  }
  return dates;
}

function formatDateFull(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Build month labels with position indices
function monthLabels(dates: string[]): { text: string; idx: number }[] {
  const labels: { text: string; idx: number }[] = [];
  let lastMonth = '';
  dates.forEach((dateStr, idx) => {
    const d = new Date(dateStr + 'T00:00:00');
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    if (month !== lastMonth) {
      labels.push({ text: month, idx });
      lastMonth = month;
    }
  });
  return labels;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface HeatmapCalendarProps {
  metrics: DailyMetrics[];
}

export default function HeatmapCalendar({ metrics }: HeatmapCalendarProps) {
  const [range, setRange] = useState<HeatmapRange>(30);
  const [tooltip, setTooltip] = useState<{
    date: string; row: MetricRow; val: number | null; x: number; y: number;
  } | null>(null);

  const metricsMap = useMemo(() => {
    const map = new Map<string, DailyMetrics>();
    for (const m of metrics) map.set(m.date, m);
    return map;
  }, [metrics]);

  const dates = useMemo(() => buildDates(range), [range]);
  const months = useMemo(() => monthLabels(dates), [dates]);

  // Cell sizing based on range
  const cellSize = range === 90 ? 6 : 14;
  const cellGap = range === 90 ? 1 : 2;
  const labelW = 52;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-arete-navy text-sm">Health Heatmap</h3>
          <p className="text-xs text-arete-muted">Daily patterns at a glance</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {([30, 90] as HeatmapRange[]).map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
                range === r
                  ? 'bg-white text-arete-navy shadow-sm'
                  : 'text-arete-muted hover:text-arete-navy'
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 pb-2">
        <div style={{ minWidth: labelW + dates.length * (cellSize + cellGap) + 8 }}>

          {/* Month labels row */}
          <div className="flex items-end" style={{ marginLeft: labelW, height: 16, marginBottom: 4 }}>
            {months.map((ml, i) => (
              <span
                key={i}
                className="text-[10px] text-arete-muted font-medium"
                style={{
                  position: 'absolute',
                  marginLeft: ml.idx * (cellSize + cellGap),
                }}
              >
                {ml.text}
              </span>
            ))}
          </div>

          {/* Metric rows */}
          {METRIC_ROWS.map(row => (
            <div key={row.key} className="flex items-center" style={{ marginBottom: cellGap }}>
              {/* Row label */}
              <div
                className="text-[10px] font-medium text-arete-muted flex-shrink-0 truncate pr-1"
                style={{ width: labelW }}
              >
                {row.label}
              </div>

              {/* Cells — one per day */}
              <div className="flex" style={{ gap: cellGap }}>
                {dates.map(dateStr => {
                  const m = metricsMap.get(dateStr);
                  const val = m ? row.getValue(m) : null;
                  const color = val != null ? row.colorScale(val) : '#F1F5F9';
                  return (
                    <div
                      key={dateStr}
                      className="rounded-[2px] flex-shrink-0 transition-all hover:ring-1 hover:ring-arete-navy/30"
                      style={{
                        width: cellSize,
                        height: cellSize,
                        backgroundColor: color,
                        cursor: 'default',
                      }}
                      onMouseEnter={e => {
                        const rect = (e.target as HTMLElement).getBoundingClientRect();
                        setTooltip({ date: dateStr, row, val, x: rect.left + rect.width / 2, y: rect.top });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 justify-center">
        <div className="flex items-center gap-1 text-[10px] text-arete-muted">
          <span>Poor</span>
          {['#EF4444', '#FB923C', '#FCD34D', '#34D399', '#059669'].map(c => (
            <div key={c} className="w-2.5 h-2.5 rounded-[2px]" style={{ backgroundColor: c }} />
          ))}
          <span>Excellent</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-arete-muted">
          <div className="w-2.5 h-2.5 rounded-[2px]" style={{ backgroundColor: '#F1F5F9' }} />
          <span>No data</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-lg border border-gray-200 px-3 py-2 pointer-events-none transform -translate-x-1/2"
          style={{
            left: tooltip.x,
            top: tooltip.y - 48,
          }}
        >
          <p className="text-xs font-semibold text-arete-navy">{formatDateFull(tooltip.date)}</p>
          <p className="text-xs text-arete-muted">
            {tooltip.row.label}: {tooltip.val != null ? tooltip.row.format(tooltip.val) : 'No data'}
          </p>
        </div>
      )}
    </div>
  );
}
